import React from 'react';

import {
  Button,
  Container,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  ThemeProvider,
  Typography,
} from '@material-ui/core';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buttonInactiveColor, kobenhavnFont } from 'theme/theme';
import 'theme/iconClasses.css';

import 'translations/translations';
import { userActions } from 'actions/userActions';
import { LanguageSwitcher } from 'components/LanguageSwitcher';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { AuthRoutes } from './Routes';

const useStyles = makeStyles((theme) => ({
  listText: {
    paddingLeft: '0.5rem',
  },
  root: {
    padding: '0',
    height: '100%',
    position: 'relative',
  },
  menuFooter: {
    position: 'absolute',
    bottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  button: {
    width: '11.8rem',
  },
  version: {
    fontSize: '0.8rem',
    textAlign: 'center',
    color: buttonInactiveColor(),
  },
}));

interface ParamTypes {
  orgId: string;
  deptId: string;
}

const Navigation: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgId, deptId } = useParams<ParamTypes>();
  const userAccess = useSelector(
    (state: ApplicationState) => state.isLoggedIn?.userAccess,
  );

  return (
    <ThemeProvider theme={kobenhavnFont}>
      <Container className={classes.root}>
        <List>
          {!orgId && userAccess?.sawmills && userAccess.sawmills.length > 0 && (
            <ListItem
              button
              component={Link}
              to={{
                pathname: AuthRoutes.SawmillStacks,
              }}
              selected
            >
              <div className={'stacks-icon'}></div>
              <ListItemText
                primary={t('navigation.stacks')}
                className={classes.listText}
              />
            </ListItem>
          )}
          {orgId && (
            <>
              <ListItem
                button
                component={Link}
                to={{
                  pathname: deptId
                    ? `/organization/${orgId}/department/${deptId}/dashboard`
                    : `/organization/${orgId}/dashboard`,
                }}
                selected={location.pathname.indexOf('dashboard') !== -1}
              >
                <div className={'dashboard-icon'}></div>
                <ListItemText
                  primary={t('navigation.dashboard')}
                  className={classes.listText}
                />
              </ListItem>
              <ListItem
                button
                component={Link}
                to={{
                  pathname: deptId
                    ? `/organization/${orgId}/department/${deptId}/stacks`
                    : `/organization/${orgId}/department/all/stacks`,
                }}
                selected={
                  location.pathname.indexOf('stacks') !== -1 ||
                  location.pathname.indexOf('stack') !== -1
                }
              >
                <div className={'stacks-icon'}></div>
                <ListItemText
                  primary={t('navigation.stacks')}
                  className={classes.listText}
                />
              </ListItem>

              <ListItem
                button
                component={Link}
                to={{
                  pathname: deptId
                    ? `/organization/${orgId}/department/${deptId}`
                    : `/organization/${orgId}`,
                }}
                selected={
                  location.pathname.indexOf('stacks') === -1 &&
                  location.pathname.indexOf('stack') === -1 &&
                  location.pathname.indexOf('dashboard') === -1
                }
              >
                <div className={'organization-details-icon--white'}></div>
                <ListItemText
                  primary={t('navigation.details')}
                  className={classes.listText}
                />
              </ListItem>
            </>
          )}
        </List>
        <div className={classes.menuFooter}>
          <LanguageSwitcher />
          <Button
            variant="contained"
            color="primary"
            disableElevation
            fullWidth
            className={classes.button}
            onClick={() => {
              dispatch(userActions.logout());
            }}
          >
            {t('login_page.log_out')}
          </Button>
          <Typography className={classes.version}>
            v. {process.env.REACT_APP_VERSION}
          </Typography>
        </div>
      </Container>
    </ThemeProvider>
  );
};

export { Navigation };
